import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Ban, CheckCheck, Clock3, Coins, FileClock, HandCoins, Info, ListChecks,
  Radio, RotateCcw, Ticket, Trophy, User, XCircle,
} from 'lucide-react';
import client from '../lib/api';
import ConfirmDialog from './components/ConfirmDialog';
import { AdminError, AdminSkeleton } from './components/PageState';
import {
  DetailPage, DetailTop, Empty, FlowList, HeroCard, LegCard, MemberCard, Note,
  Panel, Tag, badgeTone,
} from './components/BetDetailKit';
import { formatDateTime, formatMoney } from './lib/formatters';
import { PENDING_HINT, hostedBadge, legLabel } from './lib/hostedBets';
import { useAdminPermissions } from './lib/permissions';
import { SETTLE_PERMISSION } from './lib/sportsBets';
import './betDetail.css';

// Hazır spor kitabı kuponunun (Flexrix / Betby / EveryMatrix / Digitain) tam
// dökümü.
//
// Sitenin kendi kuponunun detay sayfasıyla **aynı düzendedir** (ortak kabuk
// `components/BetDetailKit`) ama kaynakları başkadır: kupon **para
// hareketinden** kurulur (kupon bizim tablomuza hiç yazılmaz), içeriği
// sağlayıcının kupon bildiriminden gelir ve ledger satırları işlem
// kimliğinden türetilen uuid5 ile bulunur.
//
// Hazır kuponu sağlayıcı kapatır; elle sonuçlandırma yalnızca kararı bizde
// uygulanabilen sağlayıcıda (Viper) çizilir. Orada karar bir **avanstır**:
// sağlayıcının sonradan göndereceği sonuç ona karşı netlenir ve oyuncu iki
// kez ödenmez (backend `viper_router` → `_open_manual`).

// Elle sonuçlandırılabilen sağlayıcılar ve karar ucu.
const MANUAL_SETTLE = {
  vipersports: (betId) => `/admin/viper/coupons/${encodeURIComponent(betId)}/settle`,
};

const MANUAL_ACTIONS = [
  {
    value: 'won',
    label: 'Kazandı',
    tone: 'ok',
    warning: 'Girdiğiniz tutar (bahis dahil toplam ödeme) bakiyeye eklenir. '
      + 'Viper\'ın sonradan göndereceği sonuç işlenmez; kararı geri alabilirsiniz.',
  },
  {
    value: 'lost',
    label: 'Kaybetti',
    tone: 'bad',
    warning: 'Para hareketi olmaz. Viper sonradan kazanç ya da bozdurma '
      + 'gönderse bile ödenmez; kararı geri alabilirsiniz.',
  },
  {
    value: 'void',
    label: 'İade',
    tone: 'warn',
    warning: 'Önce Viper\'da iptal denenir; kabul edilirse kupon orada da '
      + 'kapanır ve bahis Viper\'ın bildirimiyle gelir. Kabul edilmezse tutar '
      + 'burada iade edilir; Viper\'ın sonradan göndereceği sonuç işlenmez.',
  },
];

// Karar verilmiş kuponun düğmeleri; karar penceresi aynıdır.
const DECISION_ACTIONS = [
  {
    value: 'undo',
    label: 'Kararı geri al',
    tone: 'warn',
    path: '/undo',
    warning: 'Kararın parası tersine çevrilir ve kupon yeniden sonuç bekler; '
      + 'ardından yeni bir karar verebilirsiniz. Kazandı kararında tutar '
      + 'oyuncudan geri alınır, bakiye yetmezse işlem yapılmaz.',
  },
  {
    value: 'apply_provider',
    label: 'Viper sonucunu uygula',
    tone: 'ok',
    path: '/apply-provider',
    warning: 'Panel kararı geri alınır ve Viper\'ın gönderdiği gerçek sonuç '
      + 'işlenir.',
  },
];

const ALL_ACTIONS = [...MANUAL_ACTIONS, ...DECISION_ACTIONS];
const findAction = (value) => ALL_ACTIONS.find((item) => item.value === value);

const PROVIDER_RESULT_LABELS = { won: 'kazandı', lost: 'kaybetti', void: 'iade' };
const PROVIDER_LIVE_LABELS = {
  open: 'açık',
  won: 'kazandı',
  lost: 'kaybetti',
  void: 'iade',
  rejected: 'reddedildi',
};

// Panelin kararı sağlayıcıya gitmez ve sonradan gelen sonucu kendiliğinden
// işlenmez; yönetici gerçek durumu kupondan okuyup kararını değiştirebilir.
const manualNote = (manual, live, label) => {
  if (manual.channel === 'provider') {
    return `İade ${label} tarafına iletildi; kupon orada kapanır, bahis `
      + `${label}'ın bildirimiyle gelir.`;
  }
  const result = PROVIDER_RESULT_LABELS[manual.provider_result];
  const received = result
    ? ` ${label} sonucu "${result}" (${formatMoney(manual.provider_amount)}) `
      + 'olarak geldi, işlenmedi.'
    : '';
  const liveStatus = live
    ? ` ${label} tarafında şu an: ${PROVIDER_LIVE_LABELS[live.status] || live.status}`
      + `${Number(live.payout) > 0 ? ` (${formatMoney(live.payout)})` : ''}.`
    : '';
  if (manual.action === 'reopened') {
    return `Panel kararı geri alındı.${received}${liveStatus}`;
  }
  return `Manuel sonuçlandırıldı. ${label} sonucu dikkate alınmaz; kupon `
    + `orada ve oyuncunun ekranında açık görünebilir.${received}${liveStatus}`;
};

const LEDGER_ROLE_LABELS = {
  reversed: 'Geri alınan panel kararı',
  bet: 'Bahis (bakiyeden düşülen)',
  win: 'Kupon kazancı',
  refund: 'Bahis iadesi',
};

// Bacağın sonucu kartın sol şeridini boyar; sağlayıcının ham yazımı değil
// `hostedBadge` kümesi okunur — liste ve detay aynı kupona aynı rengi
// vermelidir.
// Sağlayıcının ham gövdesi — bacaklar çözülmüş olsa bile görülebilmelidir.
// Maç adı boş kaldığında ("Maç bilgisi yok") ilk soru "sağlayıcı onu gönderdi
// mi" olur ve yanıt bugüne kadar yalnızca sunucudaki veritabanında vardı:
// teşhis için paneli bırakıp SQL yazmak gerekiyordu. Katlanır durur, olağan
// okumada gürültü yapmaz.
const rawBody = (detail) => (
  typeof detail === 'string' ? detail : JSON.stringify(detail, null, 2)
);

const RawBody = ({ detail }) => (
  <details className="bdet-raw-wrap">
    <summary>Sağlayıcının ham gövdesi</summary>
    <pre className="bdet-raw">{rawBody(detail)}</pre>
  </details>
);

const LEG_TONES = {
  won: 'ok', half_won: 'ok', lost: 'bad', half_lost: 'bad', void: 'gray',
};

const STATUS_ICONS = {
  won: Trophy,
  half_won: Trophy,
  lost: XCircle,
  half_lost: XCircle,
  void: RotateCcw,
  cashed_out: HandCoins,
  pending: Clock3,
};

// Rozet "ne oldu"yu söyler, bu satır "parası ne oldu"yu. Kaybeden kuponda
// sağlayıcı sıfır tutarlı bir kazanç çağrısı yapar; ikisi ayrı sorulardır.
const STATUS_NOTES = {
  won: 'Sağlayıcı kuponu kazandı olarak kapattı; ödeme bakiyeye eklendi.',
  half_won: 'Kupon yarı kazandı; ödemenin bir bölümü bakiyeye eklendi.',
  lost: 'Sağlayıcı kuponu kapattı; kasadan ödeme yapılmadı.',
  half_lost: 'Kupon yarı kaybetti; bahsin bir bölümü iade edildi.',
  void: 'Kupon iptal edildi; bahis iade edildi.',
  cashed_out: 'Oyuncu kuponu sağlayıcının kendi ekranından erken kapattı.',
  pending: PENDING_HINT,
};

const SportsbookBetDetail = () => {
  const { provider, betId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [settleRequest, setSettleRequest] = useState(null);
  const [settling, setSettling] = useState(false);
  const [settleNote, setSettleNote] = useState('');
  const [settlePayout, setSettlePayout] = useState('');
  const [settleResult, setSettleResult] = useState('');
  const { can } = useAdminPermissions();
  const settleEndpoint = MANUAL_SETTLE[provider];
  const canSettle = Boolean(settleEndpoint) && can(SETTLE_PERMISSION);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await client.get(
        `/admin/sportsbook-bets/${provider}/${encodeURIComponent(betId)}/detail`,
      );
      setData(response.data);
    } catch (fault) {
      setData(null);
      setError(
        fault?.response?.status === 404
          ? 'Kupon bulunamadı. Numarası yanlış olabilir.'
          : fault?.response?.data?.detail || 'Kupon detayı yüklenemedi.',
      );
    } finally {
      setLoading(false);
    }
  }, [betId, provider]);

  useEffect(() => { load(); }, [load]);

  const closeSettle = () => {
    setSettleRequest(null);
    setSettleNote('');
    setSettlePayout('');
  };

  const runSettle = async () => {
    if (!settleRequest || !settleEndpoint) return;
    setSettling(true);
    try {
      const { path } = findAction(settleRequest);
      const { data: result } = await client.post(
        `${settleEndpoint(betId)}${path || ''}`,
        path ? { note: settleNote.trim() } : {
          action: settleRequest,
          payout: settleRequest === 'won' ? settlePayout.trim() : undefined,
          note: settleNote.trim(),
        },
      );
      const label = findAction(result.action)?.label;
      if (settleRequest === 'undo') {
        setSettleResult('Karar geri alındı; kupon yeniden sonuç bekliyor.');
      } else if (settleRequest === 'apply_provider') {
        setSettleResult('Viper sonucu uygulandı.');
      } else {
        setSettleResult(result.channel === 'provider'
          ? 'Viper iptali kabul etti; bahis iadesi Viper\'ın bildirimiyle gelecek.'
          : `Kupon "${label}" olarak kapatıldı.`);
      }
      closeSettle();
      await load();
    } catch (fault) {
      setSettleResult(
        fault?.response?.data?.detail || 'Kupon sonuçlandırılamadı.');
    } finally {
      setSettling(false);
    }
  };

  if (loading && !data) return <AdminSkeleton label="Kupon yükleniyor" />;
  if (error) return <AdminError message={error} onRetry={load} />;
  if (!data) return null;

  const { bet, user, transactions = [] } = data;
  // Sağlayıcı sonradan sonucunu gönderdiyse karar netlenmiştir; uyarı kalkar.
  const manual = data.manual_settlement?.consumed ? null : data.manual_settlement;
  // Geri alınan karar satırda durur ama kupon yeniden karar bekler.
  const decided = Boolean(manual) && manual.action !== 'reopened';
  const showManualNote = decided || Boolean(manual?.has_provider_events);
  const decisionActions = canSettle && manual?.channel === 'local'
    ? DECISION_ACTIONS.filter((action) => (
      action.value === 'undo' ? decided : manual.has_provider_events))
    : [];
  const badge = hostedBadge(bet.status);
  const tone = badgeTone(badge.cls);
  const settled = bet.status !== 'pending';
  const selections = bet.selections || [];
  const providerRows = data.provider_transactions || [];
  const StatusIcon = STATUS_ICONS[bet.status] || Clock3;
  const paidOut = Number(bet.status === 'void' ? bet.refund : bet.payout) || 0;
  // Bahsi sıfır görünen kupon bir arıza değildir: serbest bahiste sağlayıcı
  // `debit` göndermez ve elimizde yalnızca kazanç satırı kalır. Yönetici bunu
  // "eksik kayıt" sanmamalı.
  const stakeless = Number(bet.stake) === 0;

  return (
    <DetailPage tone={tone}>
      <DetailTop
        icon={Ticket}
        title="Kupon detayı"
        backTo="/admin/sports-bets"
        onRefresh={load}
        refreshing={loading}
        tags={(
          <>
            <Tag
              kind="status"
              title={bet.status === 'pending' ? PENDING_HINT : undefined}
            >
              {badge.label}
            </Tag>
            <Tag kind="hosted">{bet.provider_label}</Tag>
            {decided && manual.channel !== 'provider' && (
              <Tag kind="hosted">Manuel sonuçlandırıldı</Tag>
            )}
            {bet.is_live && (
              <Tag kind="live">
                <Radio size={11} aria-hidden="true" /> Canlı
              </Tag>
            )}
          </>
        )}
      />

      {showManualNote && (
        <Note
          tone="warn"
          icon={Info}
          actions={decisionActions.length ? decisionActions.map((action) => (
            <button
              key={action.value}
              type="button"
              onClick={() => {
                setSettleResult('');
                setSettleNote('');
                setSettleRequest(action.value);
              }}
              className={`bdet-act is-${action.tone}`}
            >
              {action.label}
            </button>
          )) : undefined}
        >
          {manualNote(manual, data.provider_live, bet.provider_label)}
        </Note>
      )}

      {canSettle && bet.status === 'pending' && !decided && (
        <Note
          tone="info"
          icon={Info}
          actions={MANUAL_ACTIONS.map((action) => (
            <button
              key={action.value}
              type="button"
              onClick={() => {
                setSettleResult('');
                setSettleNote('');
                setSettlePayout('');
                setSettleRequest(action.value);
              }}
              className={`bdet-act is-${action.tone}`}
            >
              {action.label}
            </button>
          ))}
        >
          <b>Kupon sonuçlanmadı.</b> {bet.provider_label} sonucu göndermediyse
          kararı buradan verebilirsiniz. Karar denetim izine yazılır;{' '}
          {bet.provider_label} sonradan kendi sonucunu gönderirse işlenmez ama
          karar geri alınabilir ya da gerçek sonuç uygulanabilir. Sağlayıcıya
          yalnızca <b>İade</b> iletilebilir: Kazandı ve Kaybetti kuponu orada
          kapatmaz.
        </Note>
      )}

      {!settleEndpoint && (
        <Note tone="info" icon={Info}>
          Bu kupon <b>{bet.provider_label}</b> tarafında durur; panelden
          sonuçlandırılamaz ve cashout uygulanamaz. Bize yalnızca para hareketi
          gelir — kuponun içeriği sağlayıcının kupon bildiriminden okunur.
        </Note>
      )}

      {settleResult && (
        <Note tone="warn" icon={CheckCheck}>{settleResult}</Note>
      )}

      <HeroCard
        icon={StatusIcon}
        statusLabel={badge.label}
        statusNote={decided
          ? `Manuel sonuçlandırıldı${manual.admin_username ? ` (${manual.admin_username})` : ''}.`
          : STATUS_NOTES[bet.status] || STATUS_NOTES.pending}
        idLabel={bet.bet_id}
        idTitle={`Sağlayıcı kupon numarası: ${bet.bet_id}`}
        metrics={[
          {
            label: 'Bahis',
            value: formatMoney(bet.stake),
            hint: stakeless
              ? 'Sağlayıcı bahis hareketi bildirmedi (serbest bahis olabilir)'
              : undefined,
          },
          {
            label: 'Toplam oran',
            title: 'Oran sağlayıcının kupon bildiriminden gelir; bildirim kapalıysa bilinmez',
            value: Number(bet.odds) > 0 ? Number(bet.odds).toFixed(2) : '—',
            muted: !(Number(bet.odds) > 0),
          },
          {
            label: bet.status === 'void' ? 'İade' : 'Ödeme',
            tone: paidOut > 0 ? 'ok' : undefined,
            value: formatMoney(paidOut),
            hint: bet.status === 'lost' && paidOut === 0
              ? 'Sonuçlandı, ödeme yok' : undefined,
          },
          {
            label: 'Net',
            title: 'İade edilen bahis kasadan çıkmamış sayılır ve nete girmez',
            tone: settled ? (bet.net >= 0 ? 'ok' : 'bad') : undefined,
            muted: !settled,
            value: settled ? formatMoney(bet.net) : '—',
          },
          {
            label: 'Kupon tarihi',
            muted: true,
            value: formatDateTime(bet.placed_at),
          },
          {
            label: 'Sonuç tarihi',
            muted: true,
            value: bet.settled_at ? formatDateTime(bet.settled_at) : '—',
          },
        ]}
      />

      <Panel
        icon={ListChecks}
        title="Seçimler"
        subtitle={selections.length ? `${selections.length} bacak` : 'içerik yok'}
        tone="#fbbf24"
      >
        {selections.length ? (
          <>
            <div className="bdet-legs">
              {selections.map((leg, index) => {
                const legBadge = hostedBadge(leg.result || bet.status);
                return (
                  <LegCard
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${bet.id}-${index}`}
                    index={index + 1}
                    event={leg.event || 'Maç bilgisi yok'}
                    meta={[leg.league, leg.starts_at]}
                    pickLabel={legLabel(leg)}
                    odd={Number(leg.odds) > 0 ? Number(leg.odds).toFixed(2) : '—'}
                    score={leg.score}
                    resultLabel={leg.result ? legBadge.label : 'Bekliyor'}
                    resultTone={LEG_TONES[leg.result] || 'warn'}
                    source={leg.sport || bet.provider_label}
                  />
                );
              })}
            </div>
            {bet.detail ? <RawBody detail={bet.detail} /> : null}
          </>
        ) : bet.detail ? (
          <>
            <p className="bdet-leg-note" style={{ marginBottom: 10 }}>
              Sağlayıcı bu kuponun maçlarını vermedi; elimizde yalnızca kupon
              özeti var. Uydurma bir maç adı yazılmaz.
            </p>
            <RawBody detail={bet.detail} />
          </>
        ) : (
          <Empty>
            Sağlayıcı bu kuponun içeriğini vermedi. Kupon bildirimi (webhook)
            kapalıysa maçlar ancak sağlayıcı panelinden görülebilir.
          </Empty>
        )}
      </Panel>

      <div className="bdet-cols">
        <Panel icon={Coins} title="Para hareketleri" tone="#38bdf8">
          {transactions.length ? (
            <FlowList rows={transactions} labels={LEDGER_ROLE_LABELS} />
          ) : (
            <Empty>Bu kupona bağlı para hareketi yok.</Empty>
          )}
          {bet.status === 'lost' && Number(bet.payout) === 0 && (
            <p className="bdet-hint">
              Kaybeden kuponu sağlayıcı <b>sıfır tutarlı</b> bir kazanç
              çağrısıyla kapatır; bu bir ödeme değil &quot;sonuçlandı&quot;
              bildirimidir.
            </p>
          )}
        </Panel>

        <Panel icon={User} title="Üye" tone="#a78bfa">
          {user ? (
            <MemberCard user={user}>
              {user.is_banned && (
                <p className="bdet-banned">
                  <Ban size={13} aria-hidden="true" /> Hesap yasaklı
                </p>
              )}
            </MemberCard>
          ) : (
            <Empty>Üye kaydı bulunamadı (hesap silinmiş olabilir).</Empty>
          )}
        </Panel>
      </div>

      {/* Ledger satırı yazılmamış bir olay varsa (eski kayıt, demo turu)
          yönetici yine görebilmelidir: bu liste sağlayıcının bize ne
          bildirdiğini olduğu gibi gösterir. */}
      <Panel
        icon={FileClock}
        title="Sağlayıcı hareketleri"
        subtitle="ham bildirimler"
        tone="#f472b6"
        flush
      >
        <div className="bdet-scroll">
          <table className="bdet-table">
            <thead>
              <tr>
                <th>Tarih</th>
                <th>Tür</th>
                <th>Tutar</th>
                <th>Kalan bakiye</th>
                <th>İşlem no</th>
              </tr>
            </thead>
            <tbody>
              {providerRows.map((row) => {
                const amount = Number(row.amount || 0);
                return (
                  <tr key={row.transaction_id}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {formatDateTime(row.created_at)}
                    </td>
                    <td className="is-name">
                      {LEDGER_ROLE_LABELS[row.type] || row.type}
                    </td>
                    <td className={`is-num${amount > 0 ? ' is-in' : (amount < 0 ? ' is-out' : '')}`}>
                      {formatMoney(amount)}
                    </td>
                    <td className="is-num">{formatMoney(row.balance_after)}</td>
                    <td className="is-id" title={row.transaction_id}>
                      {row.transaction_id}
                    </td>
                  </tr>
                );
              })}
              {!providerRows.length && (
                <tr>
                  <td colSpan={5}>
                    <p className="bdet-empty">Sağlayıcı hareketi bulunamadı.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>
      <ConfirmDialog
        open={!!settleRequest}
        busy={settling}
        tone={settleRequest === 'won' ? 'warning' : 'danger'}
        title={settleRequest ? `Kupon: ${findAction(settleRequest).label}` : ''}
        description={settleRequest
          ? `${findAction(settleRequest).warning} `
            + 'İşlem denetim izine yazılır.'
          : ''}
        confirmLabel="Onayla"
        extra={(
          <>
            {settleRequest === 'won' && (
              <input
                value={settlePayout}
                onChange={(event) => setSettlePayout(event.target.value)}
                placeholder="Ödenecek toplam tutar (₺, bahis dahil)"
                className="admin-input w-full"
                inputMode="decimal"
                aria-label="Ödenecek tutar"
                style={{ marginBottom: 8 }}
              />
            )}
            <input
              value={settleNote}
              onChange={(event) => setSettleNote(event.target.value)}
              placeholder="Gerekçe (isteğe bağlı, ledger notuna yazılır)"
              className="admin-input w-full"
              maxLength={255}
              aria-label="Sonuçlandırma gerekçesi"
            />
          </>
        )}
        onCancel={closeSettle}
        onConfirm={runSettle}
      />
    </DetailPage>
  );
};

export default SportsbookBetDetail;
